import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import {
  ASSIGNABLE_D365_ROLES,
  CUSTOMER,
  DMF_ENTITIES,
  DMF_FORMAT,
  DMF_PROJECTS,
  PROJECT,
  PROJECT_CONTRACT,
  PROJECT_FUNDING_SOURCE,
  PROJECT_TYPE_ENUM,
  SECURITY_USER_ROLE,
  SECURITY_USER_ROLE_ORG,
  SYSTEM_USER,
  VENDOR,
  type DmfEntityDef,
} from '@lg/d365-client';
import {
  GL_ACCOUNTANT_SLOT,
  MASTER_DATA_SHEETS,
  type Address,
  type Answers,
  type EnvProfile,
  type PeopleData,
} from '@lg/shared';
import { projectsInCreationOrder } from '../domain/people/masterData.js';

/**
 * Data management packages built from a request (spec 9.4). The same zip can
 * be imported by hand in Data management, sent to ImportFromPackage, or
 * enqueued to a recurring data job. Never contains the EIN or bank data.
 */

export interface EntityTable {
  /** DMF_ENTITIES key, for packages built from those definitions */
  key?: keyof typeof DMF_ENTITIES;
  entityName: string;
  fileName: string;
  /** Internal entity name (TargetEntity in the manifest), when known */
  targetEntity?: string;
  /** Execution order inside the package */
  sequence: number;
  columns: readonly string[];
  rows: string[][];
}

export type PackageId = 'setup' | 'people' | 'projects' | 'ledger';

export interface DataPackage {
  id: PackageId;
  definitionGroup: string;
  title: string;
  description: string;
  /** Company the job runs in */
  companyContext: string;
  entities: EntityTable[];
  notes: string[];
}

const yesNo = (b: boolean): string => (b ? DMF_FORMAT.yes : DMF_FORMAT.no);

function table(
  key: keyof typeof DMF_ENTITIES,
  sequence: number,
  rows: Array<Record<string, string>>,
): EntityTable {
  const def: DmfEntityDef = DMF_ENTITIES[key];
  return {
    key,
    entityName: def.entityName,
    fileName: def.fileName,
    ...('targetEntity' in def && def.targetEntity ? { targetEntity: def.targetEntity } : {}),
    sequence,
    columns: def.columns,
    rows: rows.map((r) => def.columns.map((c) => r[c] ?? '')),
  };
}

/**
 * D365 user ID: firstname.lastname (e.g. ben.okoro), lower case, letters and digits only in
 * each part, at most 20 characters (D365's limit, trimmed to 18 to leave room for a number),
 * unique: a repeat gets 2, 3, ….
 */
export function deriveUserIds(
  users: ReadonlyArray<{ upn: string; displayName: string }>,
  taken: ReadonlySet<string> = new Set(),
): Map<string, string> {
  const used = new Set(taken);
  const out = new Map<string, string>();
  const clean = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const u of users) {
    const parts = u.displayName.trim().split(/\s+/);
    const first = clean(parts[0] ?? '') || clean(u.upn.split('@')[0] ?? '');
    const last = parts.length > 1 ? clean(parts[parts.length - 1] ?? '') : '';
    const base = (last ? `${first}.${last}` : first).slice(0, 18).replace(/\.$/, '') || 'user';
    let candidate = base;
    for (let n = 2; used.has(candidate); n++) candidate = `${base}${n}`;
    used.add(candidate);
    out.set(u.upn, candidate);
  }
  return out;
}

export function buildSetupPackage(answers: Answers, companyId: string): DataPackage {
  const address = answers['entity.address'] as Partial<Address> | undefined;
  const legalName = String(answers['entity.legalName'] ?? '');
  const venueName = String(answers['venue.name'] ?? legalName);
  const dimension = String(answers['finance.venueDimensionValue'] ?? '');
  return {
    id: 'setup',
    definitionGroup: DMF_PROJECTS.setup,
    title: 'Venue setup',
    description: 'Creates the legal entity and its Venue financial dimension value.',
    companyContext: 'DAT',
    entities: [
      table('legalEntities', 1, [
        {
          LEGALENTITYID: companyId,
          NAME: legalName,
          SEARCHNAME: String(answers['entity.searchName'] ?? ''),
          ADDRESSDESCRIPTION: venueName,
          ADDRESSSTREET: address?.street ?? '',
          ADDRESSCITY: address?.city ?? '',
          ADDRESSSTATE: address?.state ?? '',
          ADDRESSZIPCODE: address?.zip ?? '',
          ADDRESSCOUNTRYREGIONID: DMF_FORMAT.countryRegion,
          TIMEZONE: String(answers['entity.timeZone'] ?? ''),
        },
      ]),
      table(
        'dimensionValues',
        2,
        dimension
          ? [
              {
                DIMENSIONNAME: DMF_FORMAT.dimensionName,
                DIMENSIONVALUE: dimension,
                DESCRIPTION: venueName,
                ISSUSPENDED: yesNo(false),
              },
            ]
          : [],
      ),
    ],
    notes: [
      'The EIN / tax registration is never written to a file; provisioning sets it directly.',
      'Setup data (accounts, posting profiles, number sequences) comes from the template via Copy into legal entity.',
    ],
  };
}

/**
 * What the target environment changes in the people package (from its EnvProfile, see
 * peoplePackageEnv). Without it the package is the sheet as entered.
 */
export interface PeoplePackageEnv {
  /** Pay cycle every position gets; blank where the environment has none (Legends TRN) */
  payCycleId: string;
  /**
   * An existing D365 user who holds the venue's GL accountant position instead of the person on
   * the sheet (employed in the venue, given the accountant roles here). Their worker is written
   * only when `name` is known.
   */
  accountant: { userId: string; personnelNumber: string; upn: string; name: string | null } | null;
  /** Where positions that report outside the venue report to (the environment's approver seat) */
  outsideParentPositionId: string | null;
}

export interface PeoplePackageOptions {
  takenUserIds?: ReadonlySet<string>;
  /** yyyy-mm-dd: employments, assignments and reporting lines start no later than this */
  asOf?: string;
  env?: PeoplePackageEnv;
}

/** The people package settings an environment profile implies. */
export function peoplePackageEnv(profile: EnvProfile): PeoplePackageEnv {
  return {
    payCycleId: profile.payCycle.create ? '' : profile.payCycle.id,
    accountant:
      profile.accountantUpn && profile.accountantUserId
        ? {
            userId: profile.accountantUserId,
            personnelNumber: profile.accountantPersonnelNumber,
            upn: profile.accountantUpn,
            name: profile.accountantName,
          }
        : null,
    outsideParentPositionId: profile.outsideParentPositionId,
  };
}

const splitName = (name: string): { first: string; last: string } => {
  const [first = '', ...rest] = name.trim().split(/\s+/);
  return { first: first.slice(0, 25), last: (rest.join(' ') || first).slice(0, 25) };
};

export function buildPeoplePackage(
  data: PeopleData,
  companyId: string,
  opts: PeoplePackageOptions = {},
): DataPackage {
  const userIds = deriveUserIds(data.users, opts.takenUserIds);
  const userId = (upn: string): string => userIds.get(upn) ?? upn;
  const hasUser = (upn: string): boolean => userIds.has(upn);
  const asOf = opts.asOf;
  const from = (date: string | undefined): string => (asOf && (!date || date > asOf) ? asOf : (date ?? ''));
  const env = opts.env;
  const acct = env?.accountant ?? null;
  const glPositions = new Set(
    data.positions.filter((p) => p.jobId === GL_ACCOUNTANT_SLOT.jobId).map((p) => p.positionId),
  );
  const venuePositions = new Set(data.positions.map((p) => p.positionId));
  const parentOf = (parent: string): string =>
    env?.outsideParentPositionId && !venuePositions.has(parent) ? env.outsideParentPositionId : parent;
  const firstStart = from(data.assignments[0]?.startDate ?? data.workers[0]?.employmentStart);

  // The environment's accountant takes the GL accountant seat from the person on the sheet.
  const assignments = data.assignments.map((a) =>
    acct && glPositions.has(a.positionId)
      ? { ...a, personnelNumber: acct.personnelNumber, primary: false }
      : a,
  );
  const roles = [
    ...data.roles,
    ...(acct
      ? GL_ACCOUNTANT_SLOT.roles
          .filter((roleId) => !data.roles.some((r) => r.upn === acct.upn && r.roleId === roleId))
          .map((roleId) => ({ upn: acct.upn, roleId }))
      : []),
  ];
  const roleUser = (upn: string): string => (acct && upn === acct.upn ? acct.userId : userId(upn));
  const jobs = [...new Map(data.positions.map((p) => [p.jobId, p.title])).entries()];

  const entities: EntityTable[] = [
    table(
      'users',
      1,
      data.users.map((u) => ({
        USERID: userId(u.upn),
        USERNAME: u.displayName,
        ALIAS: u.upn,
        EMAIL: u.email,
        NETWORKDOMAIN: DMF_FORMAT.networkDomain,
        ENABLED: yesNo(true),
        COMPANY: companyId,
        USERINFO_LANGUAGE: DMF_FORMAT.language,
        HELPLANGUAGE: DMF_FORMAT.language,
        ACCOUNTTYPE: SYSTEM_USER.accountType,
      })),
    ),
    table('workers', 2, [
      ...data.workers.map((w) => ({
        PERSONNELNUMBER: w.personnelNumber,
        FIRSTNAME: w.firstName,
        LASTNAME: w.lastName,
        PRIMARYCONTACTEMAIL: w.upn,
        USER: hasUser(w.upn) ? userId(w.upn) : '',
        PERSONUSERVALIDFROM: hasUser(w.upn) ? from(w.employmentStart) : '',
      })),
      ...(acct?.name
        ? [
            {
              PERSONNELNUMBER: acct.personnelNumber,
              FIRSTNAME: splitName(acct.name).first,
              LASTNAME: splitName(acct.name).last,
              PRIMARYCONTACTEMAIL: acct.upn,
              USER: acct.userId,
              PERSONUSERVALIDFROM: firstStart,
            },
          ]
        : []),
    ]),
    table('employments', 3, [
      ...data.workers.map((w) => ({
        PERSONNELNUMBER: w.personnelNumber,
        LEGALENTITYID: companyId,
        EMPLOYMENTSTARTDATE: from(w.employmentStart),
        WORKERTYPE: DMF_FORMAT.workerType,
      })),
      ...(acct
        ? [
            {
              PERSONNELNUMBER: acct.personnelNumber,
              LEGALENTITYID: companyId,
              EMPLOYMENTSTARTDATE: firstStart,
              WORKERTYPE: DMF_FORMAT.workerType,
            },
          ]
        : []),
    ]),
    table(
      'jobs',
      4,
      jobs.map(([jobId, title]) => ({ JOBID: jobId, DESCRIPTION: title })),
    ),
    table(
      'positions',
      5,
      data.positions.map((p) => ({
        POSITIONID: p.positionId,
        DESCRIPTION: p.title,
        JOBID: p.jobId,
        DEPARTMENTNUMBER: p.departmentId ?? '',
        AVAILABLEFORASSIGNMENT: '',
        PAIDBYLEGALENTITY: companyId,
        PAYCYCLEID: env?.payCycleId ?? '',
      })),
    ),
    table(
      'positionHierarchies',
      6,
      data.positions.map((p) => {
        const start = data.assignments.find((a) => a.positionId === p.positionId)?.startDate;
        return {
          POSITIONID: p.positionId,
          PARENTPOSITIONID: parentOf(p.reportsToPositionId),
          HIERARCHYTYPENAME: DMF_FORMAT.hierarchyType,
          VALIDFROM: from(start),
          VALIDTO: DMF_FORMAT.maxDate,
        };
      }),
    ),
    table(
      'positionAssignments',
      7,
      assignments.map((a) => ({
        POSITIONID: a.positionId,
        PERSONNELNUMBER: a.personnelNumber,
        VALIDFROM: from(a.startDate),
        VALIDTO: DMF_FORMAT.maxDate,
        ISPRIMARYPOSITION: yesNo(a.primary),
      })),
    ),
    table(
      'userRoles',
      8,
      roles.map((r) => ({
        USERID: roleUser(r.upn),
        SECURITYROLEIDENTIFIER: r.roleId,
        SECURITYROLENAME: ASSIGNABLE_D365_ROLES[r.roleId] ?? '',
        ASSIGNMENTSTATUS: SECURITY_USER_ROLE.enabled,
        ASSIGNMENTMODE: SECURITY_USER_ROLE.manual,
      })),
    ),
    table(
      'userRoleOrganizations',
      9,
      roles.map((r) => ({
        USERID: roleUser(r.upn),
        SECURITYROLEIDENTIFIER: r.roleId,
        ORGANIZATIONTYPE: SECURITY_USER_ROLE_ORG.legalEntity,
        ORGANIZATIONID: companyId,
        OPERATINGUNITTYPE: SECURITY_USER_ROLE_ORG.noOperatingUnit,
      })),
    ),
    table(
      'signingLimits',
      10,
      data.approvalLimits.map((l) => ({
        COMPANY: companyId,
        JOBID: l.jobId,
        DOCUMENTTYPE: l.documentType,
        APPROVALLIMIT: String(l.amountUsd),
        CURRENCYCODE: 'USD',
        EFFECTIVEDATE: firstStart,
      })),
    ),
  ];

  return {
    id: 'people',
    definitionGroup: DMF_PROJECTS.people,
    title: 'People and approvals',
    description:
      'Users, workers (linked to their users), employments, jobs, positions, reporting lines, assignments, role assignments and signing limits.',
    companyContext: companyId,
    entities,
    notes: [
      'Import into the venue company after the template copy has finished.',
      'Role assignments are scoped to the venue company only.',
      'Workers and positions keep the IDs on the sheet: the personnel number and position number sequences must allow manual numbers.',
      ...(acct
        ? [
            `${acct.userId} (worker ${acct.personnelNumber}) holds the GL accountant position and gets its roles in ${companyId}.`,
          ]
        : []),
      ...(env?.outsideParentPositionId
        ? [`Positions that report outside the venue report to ${env.outsideParentPositionId}.`]
        : []),
      'Signing limits: D365 has no entity of that name and skips the file; set the limits up by hand.',
    ],
  };
}

/** A row keyed by OData field name in upper case (the staging column); unconfirmed fields are left out. */
function odataRow(
  fields: Record<string, string | null>,
  values: Record<string, string | undefined>,
): Record<string, string> {
  const row: Record<string, string> = {};
  for (const [key, field] of Object.entries(fields)) if (field) row[field.toUpperCase()] = values[key] ?? '';
  return row;
}

/**
 * Customers, vendors, project contracts (with their customer funding source) and projects from
 * the master data tabs: the same records the importCustomersVendors / importProjects steps create
 * over OData, with the same field names as columns. Null when those tabs are empty.
 */
export function buildProjectsPackage(data: PeopleData, companyId: string): DataPackage | null {
  if (MASTER_DATA_SHEETS.every((s) => data[s].length === 0)) return null;
  const country = (a: { street?: string; city?: string; zip?: string }): string | undefined =>
    a.street || a.city || a.zip ? DMF_FORMAT.countryRegion : undefined;
  const party = (r: PeopleData['customers'][number] | PeopleData['vendors'][number]) => ({
    name: r.name,
    currency: r.currency,
    phone: r.phone,
    email: r.email,
    street: r.street,
    city: r.city,
    state: r.state,
    zip: r.zip,
    country: country(r),
    paymentTerms: r.paymentTerms,
  });
  return {
    id: 'projects',
    definitionGroup: DMF_PROJECTS.projects,
    title: 'Customers, vendors and projects',
    description:
      'The master data tabs: new customers and vendors, project contracts with their customer funding source, and the opening projects.',
    companyContext: companyId,
    entities: [
      table(
        'customers',
        1,
        data.customers.map((c) =>
          odataRow(CUSTOMER.fields, {
            ...party(c),
            account: c.customerAccount,
            group: c.customerGroup,
            partyType: CUSTOMER.organization,
          }),
        ),
      ),
      table(
        'vendors',
        2,
        data.vendors.map((v) =>
          odataRow(VENDOR.fields, {
            ...party(v),
            account: v.vendorAccount,
            group: v.vendorGroup,
            partyType: VENDOR.organization,
            paymentMethod: v.paymentMethod,
          }),
        ),
      ),
      table(
        'projectContracts',
        3,
        data.projectContracts.map((c) =>
          odataRow(PROJECT_CONTRACT.fields, { id: c.contractId, name: c.name }),
        ),
      ),
      table(
        'projectFundingSources',
        4,
        data.projectContracts.map((c) =>
          odataRow(PROJECT_FUNDING_SOURCE.fields, {
            contractId: c.contractId,
            sourceId: c.customerAccount,
            type: PROJECT_FUNDING_SOURCE.customerType,
            customer: c.customerAccount,
            defaultRule: PROJECT_FUNDING_SOURCE.yes,
          }),
        ),
      ),
      table(
        'projects',
        5,
        // Parents before sub-projects, as the OData step creates them.
        projectsInCreationOrder(data.projects).map((p) =>
          odataRow(PROJECT.fields, {
            id: p.projectId,
            name: p.name,
            group: p.projectGroup,
            type: PROJECT_TYPE_ENUM[p.projectType],
            contractId: p.contractId,
            parentId: p.parentProjectId,
            manager: p.managerPersonnelNumber,
            controller: p.controllerPersonnelNumber,
            startDate: p.startDate,
            endDate: p.endDate,
          }),
        ),
      ),
    ],
    notes: [
      'Import into the venue company after the template copy (groups) and the people package (project managers and controllers).',
      'Columns are the OData field names, so a check against /data/$metadata confirms both paths at once (VERIFY V-W7).',
    ],
  };
}

// ── zip ──────────────────────────────────────────────────────────────────────
//
// Package layout CONFIRMED 2026-10-22 against an export from the customer's environment
// (RSM-Legal-entity-export): Manifest.xml and PackageHeader.xml in UTF-16 with a BOM, namespace
// http://schemas.microsoft.com/dynamics/2015/01/DataManagement, data-contract element order
// (alphabetical), one Excel file per entity with an EntityMapList mapping each column to itself.

const csvCell = (v: string): string => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export function toCsv(columns: readonly string[], rows: readonly string[][]): string {
  return [columns, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

const xmlEscape = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const NS = 'http://schemas.microsoft.com/dynamics/2015/01/DataManagement';
const NS_I = 'http://www.w3.org/2001/XMLSchema-instance';

/** The Excel file an entity is written to inside the package (the export's naming). */
export const packageFileOf = (e: Pick<EntityTable, 'fileName'>): string =>
  e.fileName.replace(/\.csv$/i, '.xlsx');

/** Worksheet name as D365 writes it: spaces to underscores, at most 30 characters. */
const sheetNameOf = (entityName: string): string => entityName.replace(/[^A-Za-z0-9]+/g, '_').slice(0, 30);

const entityMap = (column: string): string =>
  `<EntityMap><ArrayIndex>0</ArrayIndex><EntityField>${xmlEscape(column)}</EntityField>` +
  `<EntityFieldConversionList i:nil="true" /><IsAutoDefault>false</IsAutoDefault>` +
  `<IsAutoGenerated>false</IsAutoGenerated><IsDefaultValueEqualNull>false</IsDefaultValueEqualNull>` +
  `<UseTextQualifier>false</UseTextQualifier><XMLField>${xmlEscape(column)}</XMLField></EntityMap>`;

/** One manifest entry, in the export's element order, mapping each column to itself. */
export function manifestEntityXml(e: {
  entityName: string;
  columns: readonly string[];
  inputFilePath: string;
  sequence: number;
  sourceFormat?: string;
  targetEntity?: string;
}): string {
  return (
    '<DataManagementPackageEntityData>' +
    '<DefaultRefreshType>FullPush</DefaultRefreshType><Disable>false</Disable>' +
    `<EntityMapList>${e.columns.map(entityMap).join('')}</EntityMapList>` +
    `<EntityName>${xmlEscape(e.entityName)}</EntityName>` +
    '<EntityTransformList i:nil="true" /><ExcelSheetName />' +
    '<ExecutionUnit>1</ExecutionUnit><FailExecutionUnitOnError>false</FailExecutionUnitOnError>' +
    '<FailLevelOnError>false</FailLevelOnError>' +
    `<InputFilePath>${xmlEscape(e.inputFilePath)}</InputFilePath>` +
    '<LevelInExecutionUnit>1</LevelInExecutionUnit>' +
    '<QueryData i:nil="true" /><QueryFilter i:nil="true" />' +
    '<RunBusinessLogic>true</RunBusinessLogic><RunBusinessValidation>true</RunBusinessValidation>' +
    `<SampleFilePath /><SequenceInLevel>${e.sequence}</SequenceInLevel>` +
    `<SourceFormat>${xmlEscape(e.sourceFormat ?? 'excel')}</SourceFormat>` +
    (e.targetEntity ? `<TargetEntity>${xmlEscape(e.targetEntity)}</TargetEntity>` : '') +
    '<ValidationStatus>Yes</ValidationStatus>' +
    '</DataManagementPackageEntityData>'
  );
}

export function manifestXml(pkg: DataPackage): string {
  const entities = pkg.entities
    .map((e) => manifestEntityXml({ ...e, inputFilePath: packageFileOf(e) }))
    .join('');
  return (
    `<DataManagementPackageManifest xmlns:i="${NS_I}" xmlns="${NS}">` +
    `<DefinitionGroupName>${xmlEscape(pkg.definitionGroup)}</DefinitionGroupName><Description />` +
    `<PackageEntityList>${entities}</PackageEntityList>` +
    '<ProjectCategory>0</ProjectCategory><RulesData i:nil="true" /></DataManagementPackageManifest>'
  );
}

export function packageHeaderXml(pkg: DataPackage): string {
  return (
    `<DataManagementPackageHeader xmlns:i="${NS_I}" xmlns="${NS}">` +
    `<Description>${xmlEscape(pkg.definitionGroup)}</Description>` +
    '<ManifestType>Microsoft.Dynamics.AX.Framework.Tools.DataManagement.Serialization.DataManagementPackageManifest</ManifestType>' +
    '<PackageType>DefinitionGroup</PackageType><PackageVersion>2</PackageVersion></DataManagementPackageHeader>'
  );
}

/** UTF-16LE with a BOM, as D365 writes the package XML files. */
function utf16(text: string): Uint8Array {
  const out = new Uint8Array(2 + text.length * 2);
  out.set([0xff, 0xfe]);
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    out[2 + i * 2] = c & 0xff;
    out[3 + i * 2] = c >> 8;
  }
  return out;
}

/** One entity as an Excel file: header row of staging columns, every value as text. */
const FIXED_DATE = new Date('2000-01-01T00:00:00Z');

async function entityWorkbook(e: EntityTable): Promise<Uint8Array> {
  const wb = new ExcelJS.Workbook();
  // ExcelJS stamps the current time in the document properties and on the entries of the .xlsx
  // (itself a zip): both are fixed, so the same data gives the same package.
  wb.created = wb.modified = FIXED_DATE;
  const ws = wb.addWorksheet(sheetNameOf(e.entityName));
  ws.addRow([...e.columns]);
  for (const row of e.rows) ws.addRow(row);
  const xlsx = await JSZip.loadAsync((await wb.xlsx.writeBuffer()) as ArrayBuffer);
  for (const f of Object.values(xlsx.files)) f.date = FIXED_DATE;
  return xlsx.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}

/** The data package zip, laid out like a D365 export (fixed timestamps: same data, same zip). */
export async function zipPackage(pkg: DataPackage): Promise<Uint8Array> {
  const zip = new JSZip();
  const date = new Date('2000-01-01T00:00:00Z');
  zip.file('Manifest.xml', utf16(manifestXml(pkg)), { date });
  zip.file('PackageHeader.xml', utf16(packageHeaderXml(pkg)), { date });
  for (const e of pkg.entities) zip.file(packageFileOf(e), await entityWorkbook(e), { date });
  return zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}

export const packageFileName = (pkg: DataPackage, companyId: string): string =>
  `${pkg.definitionGroup}-${companyId}.zip`;
